import { Box, Chip, Collapse, Stack, Typography } from "@mui/material";
import { useEffect, useState } from "react";
import { foyerApi, type DeviceExitEvent } from "../http/foyer";

interface Props {
  premiseId: string;
  screenId: string;
  /** From the heartbeat: a shell that cannot report says so instead of "none". */
  platform?: string;
}

const REASON_LABEL: Record<string, string> = {
  ANR: "Froze (ANR)",
  "CRASH (java)": "App crashed",
  "CRASH (native)": "App crashed (native)",
  LOW_MEMORY: "Killed: low memory",
  RENDERER_CRASH: "Page renderer crashed",
  RENDERER_KILLED: "Page renderer killed",
  EXIT_SELF: "Restarted itself",
  "USER_REQUESTED (force-stop)": "Force-stopped",
};

/**
 * Crashes, freezes and restarts the TV reported by itself on boot (kiosk
 * 0.2.49+). Before that, these were only visible inside a "Capture logs"
 * file, so a box that crashed overnight looked fine the next morning.
 */
export default function DeviceExitsPanel({
  premiseId,
  screenId,
  platform,
}: Props) {
  const [events, setEvents] = useState<DeviceExitEvent[] | null>(null);
  const [failed, setFailed] = useState(false);
  const [open, setOpen] = useState<number | null>(null);

  useEffect(() => {
    let live = true;
    setEvents(null);
    setFailed(false);
    foyerApi
      .screenExitEvents(premiseId, screenId, 14)
      .then((r) => live && setEvents(r.events))
      .catch(() => live && setFailed(true));
    return () => {
      live = false;
    };
  }, [premiseId, screenId]);

  if (failed) {
    return (
      <Typography variant="caption" color="error.main">
        Could not load crash history.
      </Typography>
    );
  }
  if (!events) return null;
  if (events.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary">
        {platform && platform !== "stb" && platform !== "philips"
          ? "Not reported on this platform (Android shell only)."
          : "No crashes or restarts reported in the last 14 days. Shells older than 0.2.49 do not report."}
      </Typography>
    );
  }
  return (
    <Stack spacing={0.5} data-testid="device-exits">
      {events.map((e, i) => (
        <Box key={`${e.at}-${i}`}>
          <Stack
            direction="row"
            spacing={1}
            alignItems="center"
            onClick={() => e.trace && setOpen(open === i ? null : i)}
            sx={{ cursor: e.trace ? "pointer" : "default" }}
          >
            <Chip
              size="small"
              color={e.fault ? "error" : "default"}
              variant={e.fault ? "filled" : "outlined"}
              label={REASON_LABEL[e.reason] ?? e.reason}
            />
            <Typography variant="body2">
              {new Date(e.at).toLocaleString()}
            </Typography>
            {e.description && (
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{
                  minWidth: 0,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                {e.description}
              </Typography>
            )}
            {e.trace && (
              <Typography variant="caption" color="primary.main">
                {open === i ? "hide trace" : "trace"}
              </Typography>
            )}
          </Stack>
          {e.trace && (
            <Collapse in={open === i} unmountOnExit>
              <Box
                component="pre"
                sx={{
                  m: 0,
                  mt: 0.5,
                  p: 1,
                  fontSize: 11,
                  maxHeight: 240,
                  overflow: "auto",
                  bgcolor: "action.hover",
                  borderRadius: 1,
                }}
              >
                {e.trace}
              </Box>
            </Collapse>
          )}
        </Box>
      ))}
    </Stack>
  );
}
